/**
 * AudioAnalyserIndicator
 *
 * Analyzes an audio stream using the Web Audio API and toggles CSS classes
 * on target elements to indicate speaking activity based on volume levels.
 */
export default class AudioAnalyserIndicator {
    /**
     * @param {object} options
     * @param {MediaStream} options.stream - The audio stream to analyze.
     * @param {AudioContext} options.audioContext - The shared AudioContext.
     * @param {jQuery} options.videoWrapperElement - jQuery object for the video wrapper div.
     * @param {jQuery} options.indicatorElement - jQuery object for the audio indicator div.
     * @param {number} [options.speakingThreshold=5] - The volume threshold to trigger the speaking state.
     */
    constructor({ stream, audioContext, videoWrapperElement, indicatorElement, speakingThreshold = 5 }) {
        if (!stream || !stream.getAudioTracks().length) {
            console.warn("AudioAnalyserIndicator: Stream is missing or has no audio tracks.");
            return; // Don't initialize if stream is invalid
        }
        if (!audioContext) {
            console.error("AudioAnalyserIndicator: AudioContext is required.");
            return;
        }
        if (!videoWrapperElement || !videoWrapperElement.length) {
             console.warn("AudioAnalyserIndicator: videoWrapperElement is missing.");
             return;
        }
        if (!indicatorElement || !indicatorElement.length) {
             console.warn("AudioAnalyserIndicator: indicatorElement is missing.");
            return;
        }

        this.stream = stream;
        this.audioContext = audioContext;
        this.videoWrapperElement = videoWrapperElement;
        this.indicatorElement = indicatorElement;
        this.speakingThreshold = speakingThreshold;

        this.analyser = null;
        this.source = null;
        this.animationFrameId = null;
        this.isActive = false;
        this.speakingTimeout = null;
        this.isSpeakingState = false;

        this._setup();
    }

    _setup() {
        try {
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            this.analyser.smoothingTimeConstant = 0.3;

            // Important: Check if the stream is still active before creating source
            if (!this.stream.active || !this.stream.getAudioTracks().some(track => track.readyState === 'live')) {
                console.warn("AudioAnalyserIndicator: Stream is not active during setup.");
                return;
            }

            this.source = this.audioContext.createMediaStreamSource(this.stream);
            this.source.connect(this.analyser);
            // Do not connect analyser to destination

            this._startAnalysis();
            this.isActive = true;
            console.log("AudioAnalyserIndicator: Setup complete and analysis started.");

        } catch (error) {
            console.error("AudioAnalyserIndicator: Error setting up analysis:", error);
            this._cleanup(); // Clean up if setup failed
        }
    }

    _startAnalysis() {
        if (!this.analyser) return;

        const dataArray = new Uint8Array(this.analyser.frequencyBinCount);

        const analyse = () => {
            if (!this.isActive || !this.analyser) return; // Stop if destroyed or analyser gone

            this.analyser.getByteFrequencyData(dataArray);

            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i];
            }
            const average = sum / dataArray.length;

            const isSpeaking = average > this.speakingThreshold;

            // Handle speaking state with delay for turning off
            this.videoWrapperElement.toggleClass('speaking', isSpeaking);
            if (isSpeaking) {
                // If speaking, immediately show indicator and clear any pending timeouts
                if (this.speakingTimeout) {
                    clearTimeout(this.speakingTimeout);
                    this.speakingTimeout = null;
                }

                if (!this.isSpeakingState) {
                    this.isSpeakingState = true;
                    this.indicatorElement.addClass('one-on-one__media__audio-indicator--active');
                }
            } else if (this.isSpeakingState && !this.speakingTimeout) {
                // If not speaking but in speaking state, set timeout to turn off after 300ms
                this.speakingTimeout = setTimeout(() => {
                    this.isSpeakingState = false;
                    this.indicatorElement.removeClass('one-on-one__media__audio-indicator--active');
                    this.speakingTimeout = null;
                }, 300);
            }

            this.animationFrameId = requestAnimationFrame(analyse);
        };

        // Cancel any previous frame request before starting a new one
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        this.animationFrameId = requestAnimationFrame(analyse);
    }

    _stopAnalysis() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Clear any pending speaking timeout
        if (this.speakingTimeout) {
            clearTimeout(this.speakingTimeout);
            this.speakingTimeout = null;
        }

        // Ensure classes are removed when stopping explicitly
        this.isSpeakingState = false;
        if (this.videoWrapperElement) this.videoWrapperElement.removeClass('speaking');
        if (this.indicatorElement) this.indicatorElement.removeClass('audio-indicator--active');
    }

    _cleanup() {
         console.log("AudioAnalyserIndicator: Cleaning up...");
        this._stopAnalysis();
        if (this.source) {
            try {
                this.source.disconnect(); // Disconnect source node
            } catch(e) {
                console.warn("AudioAnalyserIndicator: Error disconnecting source:", e);
            }
            this.source = null;
        }
        // Analyser node doesn't need explicit closing, garbage collection handles it
        this.analyser = null;
        this.isActive = false;
    }

    /**
     * Stops the analysis and cleans up resources used by this instance.
     * The shared AudioContext is NOT closed here.
     */
    destroy() {
        this._cleanup();
    }
}
